/**
 * ChoiceContainer component.
 */
import ApiConfig from '@api';
import ChannelsClassic from '@components/atoms/icons/channelsClassic';
import ChannelsIcon from '@components/atoms/icons/channels';
import ChannelViewContainer from '@components/elements/choice/channelViewContainer';
import ChannelsChoiceListContainer from '@components/atoms/container/channelsChoiceListContainer';
import FlavourConfig from '@shared/config/flavours';
import Focus from '@shared/types/enums/focus';
import List from '@components/sections/list/list';
import Pages from '@pages';
import TranslationManager from '@shared/assets/locale/translationsManager';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setFocus } from '@shared/store/slices/focus.slice';
import { useAppSelector } from '@shared/store/hooks';
import { userSelector } from '@shared/store/slices/user.slice';

/**
 * Props for the ChoiceContainer component.
 */
type Props = {
  onSelectView?: (index: number) => void;
};

/**
 * Renders a container with the channels choice views.
 */
export default function ChannelsChoiceList({ onSelectView }: Props) {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  // Dispatch
  const dispatch = useDispatch();

  // Selectors
  const { carrierId } = useAppSelector(userSelector);

  // ------------------------------------------------------------
  // Views
  // ------------------------------------------------------------

  const views = [
    {
      icon: ChannelsClassic,
      textKey: TranslationManager.get('PAGES.CHANNELS_CHOICE.CLASSIC_VIEW'),
      typeView: Pages.CLASSIC_CHANNEL,
      image: ApiConfig.urls.images.channelsClassic.replace(
        '{OPERATOR_ID}',
        carrierId || FlavourConfig.carrierId
      ),
    },
    {
      icon: ChannelsIcon,
      textKey: TranslationManager.get('PAGES.CHANNELS_CHOICE.DIAL_VIEW'),
      typeView: Pages.CHANNELS_MAIN,
      image: ApiConfig.urls.images.channelsDial.replace(
        '{OPERATOR_ID}',
        carrierId || FlavourConfig.carrierId
      ),
    },
  ];

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onEnter = useCallback(
    (index: number) => {
      onSelectView?.(index);
    },
    [onSelectView]
  );

  const outOfBounds = (focus: string | number, key?: string) => {
    if (key === 'LEFT') {
      dispatch(setFocus(Focus.MENU));
    }
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------

  return (
    <ChannelsChoiceListContainer>
      <List focus={Focus.CHANNELS_CHOICE} outOfBounds={outOfBounds}>
        {views.map((view, index) => (
          <ChannelViewContainer
            key={index}
            index={index}
            text={view.textKey}
            Icon={view.icon}
            image={view.image}
            action={() => {
              onEnter(index);
            }}
          />
        ))}
      </List>
    </ChannelsChoiceListContainer>
  );
}
